import { useState, FormEvent, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { SummaLoader } from '@/components/ui/SummaLoader'
import { colors, typography, spacing, borderRadius } from '@/lib/tokens'

type PageMode = 'login' | 'register'

export default function LoginPage() {
  const [mode, setMode] = useState<PageMode>('login')
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [username, setUsername] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const { user, hasLicense, loading, signIn, signOut, error: authError } = useAuth()
  const navigate = useNavigate()

  // Redirect if already logged in with license
  useEffect(() => {
    if (!loading && user && hasLicense) {
      navigate('/')
    }
  }, [user, hasLicense, loading, navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      await signIn(identifier, password)
    } catch {
      setIsLoading(false)
    }
  }

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    setSuccessMsg(null)

    if (!identifier.includes('@')) {
      setError('Ingresa un correo electronico valido')
      setIsLoading(false)
      return
    }
    if (password.length < 6) {
      setError('La contrasena debe tener al menos 6 caracteres')
      setIsLoading(false)
      return
    }
    if (password !== confirmPassword) {
      setError('Las contrasenas no coinciden')
      setIsLoading(false)
      return
    }
    const cleanUsername = username.trim().toLowerCase()
    if (cleanUsername.length < 3 || !/^[a-z0-9._-]+$/.test(cleanUsername)) {
      setError('El usuario debe tener al menos 3 caracteres (letras, numeros, . _ -)')
      setIsLoading(false)
      return
    }

    try {
      const usernameDoc = await getDoc(doc(db, 'usernames', cleanUsername))
      if (usernameDoc.exists()) {
        setError('Este nombre de usuario ya esta en uso')
        setIsLoading(false)
        return
      }

      const cred = await createUserWithEmailAndPassword(auth, identifier, password)
      const uid = cred.user.uid

      await setDoc(doc(db, 'users', uid), {
        email: identifier,
        username: cleanUsername,
        organization_id: '',
        status: 'pending',
        created_at: serverTimestamp(),
        created_by: 'self-registration',
      })

      await setDoc(doc(db, 'usernames', cleanUsername), {
        uid,
        email: identifier,
      })

      await signOut()

      setSuccessMsg('Tu cuenta ha sido creada. Un administrador revisara tu solicitud y te notificara cuando este activa.')
      setMode('login')
      setIdentifier('')
      setPassword('')
      setConfirmPassword('')
      setUsername('')
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message.includes('email-already-in-use')) {
          setError('Este correo ya esta registrado. Intenta iniciar sesion.')
        } else {
          setError(err.message)
        }
      } else {
        setError('Error al registrar')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const displayError = error || authError

  if (loading || isLoading) {
    return (
      <div style={{
        height: '100vh',
        background: colors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '16px',
      }}>
        <SummaLoader size={48} />
        <span style={{ color: colors.textSecondary }}>Cargando...</span>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.lg,
    }}>
      <div style={{
        width: '100%',
        maxWidth: '340px',
        background: colors.card,
        borderRadius: borderRadius.lg,
        padding: spacing.lg,
        border: `1px solid ${colors.border}`,
      }}>
        {/* Logo / Title */}
        <div style={{ textAlign: 'center', marginBottom: spacing.xl }}>
          <img
            src="/logo.svg"
            alt="SUMMA"
            style={{ height: '32px', marginBottom: spacing.md }}
          />
          <h2 style={{
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
            marginBottom: spacing.xs,
          }}>
            Focus
          </h2>
          <p style={{
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Terminal de Trading FX
          </p>
        </div>

        {/* Success message */}
        {successMsg && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid #10b981',
            borderRadius: borderRadius.sm,
            padding: spacing.md,
            marginBottom: spacing.lg,
            color: '#10b981',
            fontSize: typography.fontSize.sm,
          }}>
            {successMsg}
          </div>
        )}

        {/* Error message */}
        {displayError && (
          <div style={{
            background: `${colors.error}20`,
            border: `1px solid ${colors.error}`,
            borderRadius: borderRadius.sm,
            padding: spacing.md,
            marginBottom: spacing.lg,
            color: colors.error,
            fontSize: typography.fontSize.sm,
          }}>
            {displayError}
          </div>
        )}

        {mode === 'login' ? (
          <>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: spacing.lg }}>
                <label style={labelStyle}>Usuario o Email</label>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                  style={inputStyle}
                  placeholder="usuario o tu@email.com"
                />
              </div>

              <div style={{ marginBottom: spacing.xl }}>
                <label style={labelStyle}>Contrasena</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  style={inputStyle}
                  placeholder="********"
                />
              </div>

              <button type="submit" disabled={isLoading} style={primaryBtnStyle(isLoading)}>
                {isLoading ? 'Iniciando sesion...' : 'Iniciar Sesion'}
              </button>
            </form>

            <button
              onClick={() => { setMode('register'); setError(null); setSuccessMsg(null) }}
              style={linkBtnStyle}
            >
              Registrarme con Correo
            </button>
          </>
        ) : (
          <>
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: spacing.md }}>
                <label style={labelStyle}>Correo Electronico</label>
                <input
                  type="email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  disabled={isLoading}
                  style={inputStyle}
                  placeholder="tu@empresa.com"
                />
              </div>

              <div style={{ marginBottom: spacing.md }}>
                <label style={labelStyle}>Nombre de Usuario</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={isLoading}
                  style={inputStyle}
                  placeholder="jperez"
                />
              </div>

              <div style={{ marginBottom: spacing.md }}>
                <label style={labelStyle}>Contrasena</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  style={inputStyle}
                  placeholder="Minimo 6 caracteres"
                />
              </div>

              <div style={{ marginBottom: spacing.xl }}>
                <label style={labelStyle}>Confirmar Contrasena</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  style={inputStyle}
                  placeholder="********"
                />
              </div>

              <button type="submit" disabled={isLoading} style={primaryBtnStyle(isLoading)}>
                {isLoading ? 'Registrando...' : 'Crear Cuenta'}
              </button>
            </form>

            <button
              onClick={() => { setMode('login'); setError(null); setSuccessMsg(null) }}
              style={linkBtnStyle}
            >
              Ya tengo cuenta, Iniciar Sesion
            </button>
          </>
        )}

        {/* Footer */}
        <div style={{
          marginTop: spacing.lg,
          textAlign: 'center',
          fontSize: typography.fontSize.xs,
          color: colors.textMuted,
        }}>
          SUMMA FX
        </div>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: typography.fontSize.sm,
  color: colors.textSecondary,
  marginBottom: spacing.xs,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: '40px',
  background: colors.background,
  border: `1px solid ${colors.border}`,
  borderRadius: borderRadius.sm,
  padding: `0 ${spacing.md}`,
  color: colors.textPrimary,
  fontSize: typography.fontSize.base,
  outline: 'none',
  boxSizing: 'border-box',
}

function primaryBtnStyle(loading: boolean): React.CSSProperties {
  return {
    width: '100%',
    height: '44px',
    background: loading ? colors.border : colors.primary,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: borderRadius.sm,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'background 200ms',
  }
}

const linkBtnStyle: React.CSSProperties = {
  width: '100%',
  background: 'none',
  border: 'none',
  color: colors.primary,
  fontSize: typography.fontSize.sm,
  cursor: 'pointer',
  padding: `${spacing.md} 0`,
  marginTop: spacing.sm,
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
}
